import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

/**
 * GA4Loader — production-only Google Analytics 4 loader.
 *
 * Design goals:
 *  - Zero build-time coupling: measurement ID comes from `/api/public/branding`
 *    so operators can change it in Admin → Settings without a redeploy.
 *  - Preview stays clean: we skip loading if hostname is not the production
 *    domain OR the ID looks like a placeholder (`G-TEST…`, empty).
 *  - Single-page-app friendly: sends a `page_view` on every React route change
 *    with the fully-resolved path + title.
 *  - Global `window.vkTrack(event, params)` helper so any component can fire a
 *    custom event without importing anything.
 *
 * Env override: `REACT_APP_GA4_ID` (compile-time) beats the API-provided ID —
 * handy for local debugging.
 */

const PLACEHOLDER_PREFIXES = ["G-TEST", "G-XXXX", ""];
const PROD_HOSTNAMES = ["venturekhoj.com", "www.venturekhoj.com"];

function isProdHost() {
  if (typeof window === "undefined") return false;
  // Allow ?ga=1 query flag to force-enable during smoke tests.
  try {
    const u = new URL(window.location.href);
    if (u.searchParams.get("ga") === "1") return true;
  } catch (_) { /* noop */ }
  return PROD_HOSTNAMES.includes(window.location.hostname);
}

/**
 * Persist a page_view event to our own `analytics_events` collection so the
 * admin KPI dashboard sees an accurate visitor count. Runs on EVERY host
 * (including preview) so signup / OTP / report metrics on preview also line
 * up. Fire-and-forget with `keepalive:true` so nav-away doesn't drop the
 * request.
 */
function _sessionId() {
  try {
    let s = localStorage.getItem("vk_session_id");
    if (!s) {
      s = ("10000000-1000-4000-8000-100000000000").replace(/[018]/g, (c) =>
        // eslint-disable-next-line no-mixed-operators
        (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
      );
      localStorage.setItem("vk_session_id", s);
    }
    return s;
  } catch (_) { return null; }
}

function _postPageView() {
  try {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const body = {
      event: "page_view",
      path: url.pathname + url.search,
      referrer: document.referrer || null,
      session_id: _sessionId(),
      host: url.hostname || null,
      lang: document.documentElement.lang || null,
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
      utm_term: params.get("utm_term"),
      utm_content: params.get("utm_content"),
      meta: { title: document.title?.slice(0, 200) },
    };
    // Ad-blocker-resistant path first (`/api/_vk/pv`) — many privacy
    // extensions block generic `/analytics/*` and `/track` URLs. Use
    // sendBeacon when available so the request survives page unload and
    // is less likely to be surfaced as an XHR that filters inspect.
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const primary = `${backend}/api/_vk/pv`;
    const fallback = `${backend}/api/analytics/track`;
    const json = JSON.stringify(body);
    let sent = false;
    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([json], { type: "application/json" });
        sent = navigator.sendBeacon(primary, blob);
      }
    } catch (_) { /* fall through to fetch */ }
    if (!sent) {
      try {
        fetch(primary, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: json,
          keepalive: true,
          credentials: "omit",
        }).catch(() => {
          // final fallback via axios to the legacy path
          api.post("/analytics/track", body).catch(() => {});
        });
      } catch (_) {
        api.post("/analytics/track", body).catch(() => {});
      }
    }
  } catch (_) { /* noop */ }
}

function isValidId(id) {
  return typeof id === "string" &&
    id.startsWith("G-") &&
    !PLACEHOLDER_PREFIXES.some((p) => p && id.startsWith(p));
}

function injectGtag(id, awId) {
  if (typeof document === "undefined") return;
  if (document.getElementById("ga4-loader")) return;

  const s = document.createElement("script");
  s.async = true;
  s.id = "ga4-loader";
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  // send_page_view:false — we send our own page_view on every route change.
  window.gtag("config", id, { send_page_view: false });
  // Google Ads dual-config — required to fire conversion events. Same tag
  // library; a second config() call registers the AW-... property.
  if (awId && /^AW-\d+/.test(awId)) {
    window.gtag("config", awId);
  }
}

/** Public helper so any component can fire GA events without importing.
 * Dual-writes: (1) gtag → GA4 dashboard, (2) our own `analytics_events`
 * collection so the Admin → GA4 diagnostics table stays accurate even when
 * gtag is blocked or the GA4 property config is broken.
 */
if (typeof window !== "undefined") {
  window.vkTrack = (event, params = {}) => {
    try { window.gtag && window.gtag("event", event, params); } catch (_) { /* noop */ }
    // Server-side mirror — fire-and-forget, ad-blocker-resistant path.
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const body = JSON.stringify({
        event: String(event || "").slice(0, 64),
        path: window.location.pathname + window.location.search,
        host: window.location.hostname || null,
        session_id: (() => { try { return localStorage.getItem("vk_session_id"); } catch (_) { return null; } })(),
        meta: params || {},
      });
      const url = `${backend}/api/_vk/pv`;
      if (navigator.sendBeacon) {
        try { navigator.sendBeacon(url, new Blob([body], { type: "application/json" })); return; }
        catch (_) { /* fall through */ }
      }
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
        credentials: "omit",
      }).catch(() => {});
    } catch (_) { /* noop */ }
  };

  /**
   * R109.5 — Central purchase event fanout for Google Ads + GA4.
   * Fires BOTH:
   *   • gtag('event', 'purchase',  {...})           → GA4 e-commerce event
   *     (auto-imports into Google Ads when accounts are linked)
   *   • gtag('event', 'conversion', {send_to: 'AW-.../label', ...}) →
   *     direct Google Ads conversion (works standalone).
   * Both use the SAME transaction_id so Google dedupes duplicates.
   *
   * Params:
   *   transaction_id  — Cashfree order_id (or 'free' / 'mock_...')
   *   value           — amount in INR (not paise)
   *   currency        — 'INR'
   *   item_id, item_name, item_category — e-commerce item metadata
   *   coupon          — optional coupon code applied
   */
  window.vkPurchaseEvent = ({
    transaction_id, value, currency = "INR",
    item_id, item_name, item_category, coupon,
  }) => {
    if (!window.gtag) return;
    const items = item_id ? [{
      item_id, item_name: item_name || item_id, item_category: item_category || "report",
      price: Number(value) || 0, quantity: 1,
    }] : undefined;
    // GA4 purchase — imports into Google Ads via linked accounts.
    try {
      window.gtag("event", "purchase", {
        transaction_id: String(transaction_id || ""),
        value: Number(value) || 0,
        currency,
        coupon: coupon || undefined,
        items,
      });
    } catch (_) { /* noop */ }
    // Direct Google Ads conversion — belt-and-braces so it fires even if
    // the GA4→Ads import is delayed. Guarded by env config so we don't
    // send junk conversions when the label is missing.
    const AW_ID = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID;
    const AW_LABEL = process.env.REACT_APP_GOOGLE_ADS_PURCHASE_LABEL;
    if (AW_ID && AW_LABEL && /^AW-\d+/.test(AW_ID)) {
      try {
        window.gtag("event", "conversion", {
          send_to: `${AW_ID}/${AW_LABEL}`,
          value: Number(value) || 0,
          currency,
          transaction_id: String(transaction_id || ""),
        });
      } catch (_) { /* noop */ }
    }
  };
}

export default function GA4Loader() {
  const location = useLocation();
  const idRef = useRef(null);
  const readyRef = useRef(false);

  // Boot — fetch measurement ID once, then inject the tag if the gates pass.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const envId = process.env.REACT_APP_GA4_ID;
      let id = envId;
      if (!id) {
        try {
          const { data } = await api.get("/public/branding");
          id = data?.analytics?.ga4_measurement_id || "";
        } catch (_) { /* keep id empty */ }
      }
      if (cancelled) return;
      if (!isProdHost()) {
        // Silently no-op on preview so we don't pollute analytics.
        // eslint-disable-next-line no-console
        console.info("[GA4] skipped — non-prod host", window.location.hostname);
        return;
      }
      if (!isValidId(id)) {
        // eslint-disable-next-line no-console
        console.info("[GA4] skipped — invalid or placeholder id:", id);
        return;
      }
      idRef.current = id;
      injectGtag(id, process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID);
      readyRef.current = true;
      // First page_view for the initial URL (config had send_page_view:false).
      try {
        window.gtag("event", "page_view", {
          page_location: window.location.href,
          page_path: window.location.pathname + window.location.search,
          page_title: document.title,
        });
      } catch (_) { /* noop */ }
    })();
    // Always persist a page_view to our own analytics_events collection so
    // the admin dashboard's Visitors counter reflects reality regardless of
    // GA4 config. Runs on every host including preview.
    _postPageView();
    return () => { cancelled = true; };
  }, []);

  // SPA route change → page_view.
  useEffect(() => {
    // GA4 branch — only when ready + gtag loaded.
    if (readyRef.current && window.gtag) {
      Promise.resolve().then(() => {
        try {
          window.gtag("event", "page_view", {
            page_location: window.location.href,
            page_path: location.pathname + location.search,
            page_title: document.title,
          });
        } catch (_) { /* noop */ }
      });
    }
    // Server-side analytics — always fires so KPI dashboard stays accurate.
    _postPageView();
  }, [location.pathname, location.search]);

  return null;
}
